import { Check, FilmStrip, Sparkle, TrayArrowDown } from '@phosphor-icons/react';
import type { CompetitorPostDto } from '@reelplicate/api-client/model';
import { Badge, Button, Inline, Stack } from '@reelplicate/ui';
import { Link as RouterLink } from 'react-router';

import { formatLift, platformName, postedAgo, views } from './format';

/**
 * One competitor post beating its account's usual views: the cover, how many
 * times their usual it got (the number that matters), whose it is and when,
 * and the two things to do with it.
 */
export function CompetitorPostCard({
  post,
  now,
  busy,
  onSave,
  onMakeMyVersion,
}: {
  post: CompetitorPostDto;
  now: number;
  /** Saving or starting a flow from this post. */
  busy: 'save' | 'remake' | null;
  onSave: () => void;
  onMakeMyVersion: () => void;
}) {
  const meta = [
    `@${post.authorHandle}`,
    platformName(post.platform),
    postedAgo(post.postedAt, now),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="bg-bg-surface border-border-subtle flex flex-col overflow-hidden rounded-xl border">
      <a
        href={post.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open @${post.authorHandle}’s post`}
        className="bg-bg-subtle focus-visible:shadow-focus-ring relative flex aspect-[4/5] items-center justify-center outline-none"
      >
        {post.thumbnailUrl ? (
          <img
            src={post.thumbnailUrl}
            alt=""
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <FilmStrip aria-hidden className="text-icon-tertiary size-8" />
        )}
        {post.takingOff ? (
          <Badge tone="success" className="absolute top-3 left-3 shadow-sm">
            Taking off
          </Badge>
        ) : (
          post.lift !== null && (
            <Badge tone="success" className="absolute top-3 left-3 shadow-sm">
              {formatLift(post.lift)} their usual
            </Badge>
          )
        )}
      </a>
      <Stack gap={3} className="flex-1 p-4">
        <Stack gap={1}>
          <p className="text-caption text-text-secondary">{meta}</p>
          {post.caption && (
            <p className="text-body-sm text-text-primary line-clamp-2">{post.caption}</p>
          )}
        </Stack>
        {post.viewsPerHour !== null && post.viewsPerHour > 0 && post.pace !== null && (
          <p className="text-body-sm text-status-success-fg">
            +{views(Math.round(post.viewsPerHour * 24))} views a day ·{' '}
            {formatLift(post.pace)} a usual post a day
          </p>
        )}
        <p className="text-body-sm text-text-secondary">
          <span className="text-body-sm-strong text-text-primary">
            {views(post.views)} views
          </span>
          {post.baselineViews !== null && ` · usually ${views(post.baselineViews)}`}
          {post.engagementRate !== null &&
            ` · ${(post.engagementRate * 100).toFixed(1)}% engaged`}
        </p>
        <Inline gap={2} wrap className="mt-auto">
          {post.savedAssetId ? (
            <Button asChild size="sm" variant="ghost" leadingIcon={Check}>
              <RouterLink to={`/media?asset=${post.savedAssetId}`}>
                In your library
              </RouterLink>
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              leadingIcon={TrayArrowDown}
              loading={busy === 'save'}
              disabled={busy !== null}
              onClick={onSave}
            >
              Save
            </Button>
          )}
          <Button
            size="sm"
            leadingIcon={Sparkle}
            loading={busy === 'remake'}
            disabled={busy !== null}
            onClick={onMakeMyVersion}
          >
            Make my version
          </Button>
        </Inline>
      </Stack>
    </li>
  );
}
